/**
 * Profile Page
 *
 * User profile management page showing account details and settings.
 */

import { useState } from 'react';
import {
  User,
  Mail,
  Phone,
  Shield,
  Calendar,
  Edit2,
  Save,
  X,
  Key,
  Clock,
  Eye,
  EyeOff,
  UserCheck,
  AtSign,
  Fingerprint,
} from 'lucide-react';

import {
  Card,
  CardBody,
  Button,
  Input,
  Badge,
  Modal,
} from '@/components/common';
import { useAuthStore } from '@/store';
import { useToast } from '@/hooks/useToast';
import { formatDateTime } from '@/utils/date.utils';
import { userService } from '@/api/services/user.service';

// ============================================
// Change Password Modal
// ============================================

interface ChangePasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ChangePasswordModal({ open, onOpenChange }: ChangePasswordModalProps) {
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one number';
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // TODO: Implement backend API for password change
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success('Password changed successfully');
      onOpenChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      toast.error('Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    onOpenChange(false);
  };

  const eyeButton = (show: boolean, toggle: () => void) => (
    <button
      type="button"
      tabIndex={-1}
      onClick={toggle}
      className="text-foreground-muted hover:text-foreground dark:hover:text-foreground-dark"
    >
      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title="Change Password"
      size="sm"
    >
      <div className="space-y-4">
        <Input
          type={showCurrent ? 'text' : 'password'}
          label="Current Password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          error={errors.currentPassword}
          rightElement={eyeButton(showCurrent, () => setShowCurrent((v) => !v))}
          required
        />
        <Input
          type={showNew ? 'text' : 'password'}
          label="New Password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          error={errors.newPassword}
          helperText="At least 8 characters with uppercase, lowercase, and number"
          rightElement={eyeButton(showNew, () => setShowNew((v) => !v))}
          required
        />
        <Input
          type={showConfirm ? 'text' : 'password'}
          label="Confirm New Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={errors.confirmPassword}
          rightElement={eyeButton(showConfirm, () => setShowConfirm((v) => !v))}
          required
        />
        <div className="flex gap-2 justify-end pt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isSubmitting}>
            Change Password
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================
// Page Component
// ============================================

export function ProfilePage() {
  const toast = useToast();
  const user = useAuthStore((state) => state.user);
  const fetchCurrentUser = useAuthStore((state) => state.fetchCurrentUser);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Edit state
  const [editFirstName, setEditFirstName] = useState(user?.firstName || '');
  const [editLastName, setEditLastName] = useState(user?.lastName || '');
  const [editMobile, setEditMobile] = useState(user?.mobile || '');

  const handleEdit = () => {
    setEditFirstName(user?.firstName || '');
    setEditLastName(user?.lastName || '');
    setEditMobile(user?.mobile || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!user?.id) {
      toast.error('User ID not found');
      return;
    }

    setIsSaving(true);
    try {
      // Update user profile
      await userService.updateUser({
        id: user.id,
        firstName: editFirstName,
        lastName: editLastName,
        mobile: editMobile,
      });

      // Refresh current user data
      await fetchCurrentUser();

      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      const message = error instanceof Error ? error.message : 'Failed to update profile';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const getRoleDisplayName = (role?: string) => {
    switch (role) {
      case 'ROLE_SADMIN':
        return 'Super Admin';
      case 'ROLE_ADMIN':
        return 'Admin';
      case 'ROLE_SUPPORT':
        return 'Support';
      case 'ROLE_USER':
        return 'User';
      default:
        return role || 'Unknown';
    }
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const initials = [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?';

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            My Profile
          </h1>
          <p className="mt-0.5 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Manage your account information and preferences
          </p>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancel} leftIcon={<X className="h-4 w-4" />}>
                Cancel
              </Button>
              <Button onClick={handleSave} loading={isSaving} leftIcon={<Save className="h-4 w-4" />}>
                Save Changes
              </Button>
            </>
          ) : (
            <Button onClick={handleEdit} leftIcon={<Edit2 className="h-4 w-4" />}>
              Edit Profile
            </Button>
          )}
        </div>
      </div>

      {/* Profile Hero Card */}
      <Card className="overflow-hidden">
        <CardBody className="p-0">
          {/* Gradient banner */}
          <div className="h-32 bg-gradient-to-r from-primary-600 via-primary-500 to-primary-400 relative">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImEiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjMwIiBjeT0iMzAiIHI9IjEuNSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2EpIi8+PC9zdmc+')] opacity-60" />
            {/* Name & status on the banner - white text for contrast */}
            <div className="absolute bottom-4 left-32 right-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white drop-shadow-sm truncate">
                  {fullName || user?.username}
                </h2>
                <div className="mt-0.5 flex items-center gap-3 text-sm text-white/80">
                  <span className="flex items-center gap-1">
                    <AtSign className="h-3.5 w-3.5" />
                    {user?.username}
                  </span>
                  {user?.orgUserType && (
                    <span className="flex items-center gap-1">
                      <UserCheck className="h-3.5 w-3.5" />
                      {user.orgUserType}
                    </span>
                  )}
                </div>
              </div>
              <Badge variant={user?.userStatus === 'ACTIVE' ? 'success' : 'danger'} dot>
                {user?.userStatus === 'ACTIVE' ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>

          {/* Below banner: avatar + role badges */}
          <div className="px-6 py-4 -mt-8 relative flex items-end gap-4">
            {/* Avatar overlapping the banner */}
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border-4 border-card dark:border-card-dark bg-primary-100 dark:bg-primary-900/50 shadow-md">
              {user?.profileImage ? (
                <img
                  src={user.profileImage}
                  alt={user.username}
                  className="h-full w-full rounded-xl object-cover"
                />
              ) : (
                <span className="text-2xl font-bold text-primary-700 dark:text-primary-300">
                  {initials}
                </span>
              )}
            </div>

            {/* Role badges - sit below banner on white background */}
            <div className="flex flex-wrap gap-2 pt-6">
              {user?.listOfRoles?.map((role) => (
                <span
                  key={role}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary-50 dark:bg-primary-900/30 px-3 py-1.5 text-xs font-semibold text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                >
                  <Shield className="h-3.5 w-3.5" />
                  {getRoleDisplayName(role)}
                </span>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Info Cards Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="group hover:shadow-md transition-shadow">
          <CardBody className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary">
                <Mail className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                  Email
                </p>
                <p className="text-sm font-semibold text-foreground dark:text-foreground-dark truncate" title={user?.email}>
                  {user?.email || 'N/A'}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="group hover:shadow-md transition-shadow">
          <CardBody className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-50 dark:bg-success-500/10 text-success">
                <Phone className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                  Mobile
                </p>
                <p className="text-sm font-semibold text-foreground dark:text-foreground-dark">
                  {user?.mobile || 'N/A'}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="group hover:shadow-md transition-shadow">
          <CardBody className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-warning-50 dark:bg-warning-500/10 text-warning">
                <Calendar className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                  Created
                </p>
                <p className="text-sm font-semibold text-foreground dark:text-foreground-dark">
                  {user?.createdDate ? formatDateTime(user.createdDate) : 'N/A'}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="group hover:shadow-md transition-shadow">
          <CardBody className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-danger-50 dark:bg-danger-500/10 text-danger">
                <Clock className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                  Last Updated
                </p>
                <p className="text-sm font-semibold text-foreground dark:text-foreground-dark">
                  {user?.updatedDate ? formatDateTime(user.updatedDate) : 'N/A'}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Personal Details / Edit Section */}
      <Card>
        <CardBody className="p-0">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border dark:border-border-dark">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary">
                <User className="h-4 w-4" />
              </div>
              <h3 className="text-sm font-semibold text-foreground dark:text-foreground-dark">
                Personal Details
              </h3>
            </div>
          </div>

          {isEditing ? (
            <div className="p-6 space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <Input
                  label="First Name"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  required
                />
                <Input
                  label="Last Name"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                />
                <Input
                  label="Mobile"
                  value={editMobile}
                  onChange={(e) => setEditMobile(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 divide-x-0 divide-y sm:divide-y-0 divide-border dark:divide-border-dark">
              <div className="px-6 py-4">
                <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                  First Name
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground dark:text-foreground-dark">
                  {user?.firstName || 'N/A'}
                </p>
              </div>
              <div className="px-6 py-4">
                <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                  Last Name
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground dark:text-foreground-dark">
                  {user?.lastName || 'N/A'}
                </p>
              </div>
              <div className="px-6 py-4">
                <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                  Gender
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground dark:text-foreground-dark">
                  {user?.gender || 'N/A'}
                </p>
              </div>
              <div className="px-6 py-4">
                <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                  User ID
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground dark:text-foreground-dark truncate" title={user?.id}>
                  {user?.id || 'N/A'}
                </p>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Security Card */}
      <Card>
        <CardBody className="p-0">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border dark:border-border-dark">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-warning-50 dark:bg-warning-500/10 text-warning">
                <Fingerprint className="h-4 w-4" />
              </div>
              <h3 className="text-sm font-semibold text-foreground dark:text-foreground-dark">
                Security
              </h3>
            </div>
          </div>
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground dark:text-foreground-dark">
                  Password
                </p>
                <p className="text-xs text-foreground-muted dark:text-foreground-dark-muted mt-0.5">
                  Last changed: Unknown
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Key className="h-3.5 w-3.5" />}
                onClick={() => setShowPasswordModal(true)}
              >
                Change Password
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={showPasswordModal}
        onOpenChange={setShowPasswordModal}
      />
    </div>
  );
}

export default ProfilePage;
